/** @jsx jsx */
import { colors } from '@apollo/brand';
import { gql, useQuery } from '@apollo/client';
import IconHelp from '@apollo/icons/default/IconHelp.svg';
import IconGitHubSolid from '@apollo/icons/large/IconGitHubSolid.svg';
import IconTwitterSolid from '@apollo/icons/large/IconTwitterSolid.svg';
import IconYouTubeSolid from '@apollo/icons/large/IconYouTubeSolid.svg';
import {
  Button,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import { captureEvent } from '@sentry/react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { URLModal } from 'src/components/common/URLModal';
import { IsCloudEndpointInitiatingContextProvider } from 'src/hooks/useIsCloudEndpointInitiating';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayClosedLocation } from 'src/lib/routing';

import { HeadersManagerContextProvider } from '../graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { ReachabilityProvider } from '../graph/hooks/useEndpointReachability';
import { useGraphRef } from '../graph/hooks/useGraphRef';
import { RegisteredSchemaProvider } from '../graph/hooks/useSchema';

import { GraphCard } from './AtlasGraphCard';
import { AtlasGraphPeekModal } from './AtlasGraphPeekModal';

type PubliclyListedGraphConfig = {
  graphId: string;
  graphVariant: string;
  defaultQuery?: string;
  header?: PublicGraphRequiredHeader;
};

interface PublicGraphRequiredHeader {
  headerName: string;
  headerDescription: string;
  headerTemplate: string;
}

export const AtlasLayout = () => {
  const { data } = useQuery(gql<
    GraphQLTypes.AtlasLayoutQuery,
    GraphQLTypes.AtlasLayoutQueryVariables
  >`
    query AtlasLayoutQuery {
      publiclyListedVariants {
        id
        name
        isFavoriteOfCurrentUser
        url
        activeSchemaPublish {
          id
          createdAt
          schema {
            createdAt
            hash
            typeCount
            fieldCount
          }
        }
        graph {
          id
          title
          description
          avatarUrl(size: 400)
        }
      }
    }
  `);

  const [publiclyListedGraphConfigs, setPubliclyListedGraphConfigs] = useState<
    PubliclyListedGraphConfig[]
  >([]);

  const location = useLocation();

  const graphRef = useGraphRef();
  const graphListingConfig =
    graphRef &&
    publiclyListedGraphConfigs.find(
      (g) =>
        g.graphId === graphRef.graphId &&
        g.graphVariant === graphRef.graphVariant,
    );

  useEffect(() => {
    fetch(
      'https://raw.githubusercontent.com/apollographql/apollo-studio-community/main/preview-docs/AtlasGraphs.json',
    )
      .then(async (res) => {
        const contents = await res.json();
        if (contents) setPubliclyListedGraphConfigs(contents);
      })
      .catch(() => captureEvent(new Error('Failed to fetch graph atlas')));
  }, []);

  return (
    <div className="relative flex flex-1 flex-col justify-between overflow-y-auto bg-primary">
      <URLModal name={Config.modals.atlasGraphPeek}>
        {({ isOpen, closeModal }) =>
          graphRef && (
            <IsCloudEndpointInitiatingContextProvider graphRef={graphRef}>
              <HeadersManagerContextProvider graphRef={graphRef}>
                <ReachabilityProvider>
                  <RegisteredSchemaProvider graphRef={graphRef}>
                    <AtlasGraphPeekModal
                      isOpen={isOpen}
                      onClose={() => {
                        setOverlayClosedLocation(location, [
                          Config.queryParameters.GraphRef,
                        ]);
                        closeModal();
                      }}
                      defaultQuery={graphListingConfig?.defaultQuery}
                      requiredHeader={graphListingConfig?.header}
                    />
                  </RegisteredSchemaProvider>
                </ReachabilityProvider>
              </HeadersManagerContextProvider>
            </IsCloudEndpointInitiatingContextProvider>
          )
        }
      </URLModal>
      <div>
        <div
          className="flex h-72 items-center justify-center text-white"
          css={css`
            background: linear-gradient(
              to bottom right,
              ${colors.tokens.brand.primary.base},
              ${colors.tokens.brand.primary.dark},
              ${colors.tokens.brand.secondary.base}
            );
          `}
        >
          <div className="flex flex-col justify-center gap-8">
            <h1 className="mx-8 max-w-xl text-center text-3xl font-bold leading-tight lg:text-5xl">
              Explore the world's graphs & start building
            </h1>
            <Popover variant="tooltip" trigger="hover">
              <PopoverTrigger>
                <Button
                  className="mx-auto w-fit"
                  variant="secondary"
                  leftIcon={<IconHelp />}
                  type="button"
                >
                  What's this all about?
                </Button>
              </PopoverTrigger>
              {/* @ts-expect-error TODO: figure out why types are "too complicated" */}
              <PopoverContent>
                <PopoverHeader>
                  📍 Introducing the Apollo Graph Directory
                </PopoverHeader>
                <PopoverBody>
                  A curated list of publicly available GraphQL APIs! Explore
                  their schemas using the amazing Apollo Studio experience and
                  run operations in Explorer, Apollo's powerful GraphQL IDE. And
                  soon you will be able to publish your own public GraphQL APIs
                  to the Apollo Directory and share them with the world!
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl items-center place-self-start px-8 pb-2 pt-12">
          <div className="flex-1 border-t border-primary" />
          <h2 className="mx-4 flex-none text-lg font-semibold uppercase tracking-widest text-primary">
            Featured
          </h2>
          <div className="flex-1 border-t border-primary" />
        </div>
        <div className="mx-auto grid max-w-7xl grid-rows-none gap-8 p-8 lg:grid-cols-3">
          {data?.publiclyListedVariants?.map(
            (v) => v && <GraphCard key={v.id} variant={v} />,
          )}
        </div>
      </div>
      <div
        className="p-8 text-center text-inverted lg:flex lg:items-center lg:justify-center lg:py-14"
        css={css`
          background: linear-gradient(
            to top left,
            ${colors.tokens.brand.primary.base},
            ${colors.tokens.brand.primary.dark},
            ${colors.tokens.brand.secondary.base}
          );
        `}
      >
        <div className="items-center lg:flex">
          <nav className="my-6 flex flex-row justify-center gap-6 font-semibold lg:my-0">
            <Link
              className="transition-all hover:opacity-75"
              to="https://www.apollographql.com/platform"
            >
              Platform
            </Link>
            <Link
              className="transition-all hover:opacity-75"
              to="https://www.apollographql.com/enterprise/"
            >
              Enterprise
            </Link>
            <Link
              className="transition-all hover:opacity-75"
              to="https://www.apollographql.com/docs/"
            >
              Docs
            </Link>
            <Link
              className="transition-all hover:opacity-75"
              to="https://www.apollographql.com/careers"
            >
              Careers
            </Link>
            <Link
              className="transition-all hover:opacity-75"
              to="https://www.apollographql.com/blog/"
            >
              Blog
            </Link>
            <Link
              className="transition-all hover:opacity-75"
              to="https://community.apollographql.com/"
            >
              Community
            </Link>
            <Link
              className="transition-all hover:opacity-75"
              to="https://www.apollographql.com/contact-sales/"
            >
              Contact an Expert
            </Link>
          </nav>
        </div>
        <div className="flex justify-center gap-8 font-semibold lg:ml-auto">
          <Link
            className="transition-all hover:opacity-75"
            to="https://github.com/apollographql"
          >
            <IconGitHubSolid />
          </Link>
          <Link
            className="transition-all hover:opacity-75"
            to="https://twitter.com/apollographql"
          >
            <IconTwitterSolid />
          </Link>
          <Link
            className="transition-all hover:opacity-75"
            to="https://www.youtube.com/c/ApolloGraphQL"
          >
            <IconYouTubeSolid />
          </Link>
        </div>
      </div>
    </div>
  );
};
